import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { auth } from "../lib/auth";
import { api } from "../lib/api";
import { safeReturnTo } from "../lib/navigation";
import { ROUTES } from "../routes/paths";
import styles from "./ForceLoginModal.module.css";

interface ForceLoginModalProps {
  onSuccess?: () => void;
}

export function ForceLoginModal({ onSuccess }: ForceLoginModalProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if we are on an auth page, if so, do not render the modal
  const isOnAuthScreen =
    location.pathname === ROUTES.SUBSCRIPTIONS.ATOM_PATH ||
    location.pathname.startsWith("/login") ||
    location.pathname.startsWith("/register") ||
    location.pathname.startsWith("/forgot-password") ||
    location.pathname.startsWith("/reset-password") ||
    location.pathname.startsWith("/add-phone") ||
    location.pathname.startsWith("/add-password") ||
    location.pathname.startsWith("/screen/AddPasswordScreen") ||
    location.pathname.startsWith("/auth/");

  const hasToken = !!auth.access();

  if (hasToken || isOnAuthScreen) {
    return null;
  }

  const handleRegister = () => {
    navigate(ROUTES.AUTH.REGISTER, {
      state: { from: `${location.pathname}${location.search}${location.hash}` },
    });
  };

  const handleLogin = () => {
    navigate(ROUTES.AUTH.LOGIN, {
      state: { from: `${location.pathname}${location.search}${location.hash}` },
    });
  };

  const handleGuest = async () => {
    setPending(true);
    setError(null);
    try {
      const response = await api.continueAsGuest();
      auth.save({
        access: response.tokens.access,
        refresh: response.tokens.refresh || "",
        user: response.user,
      });
      if (onSuccess) {
        onSuccess();
      } else {
        // Reload or assign path to refresh session globally
        window.location.assign(safeReturnTo(`${location.pathname}${location.search}${location.hash}`));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to continue as guest.");
    } finally {
      setPending(false);
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.bottomSheet}>
        {/* Lock icon */}
        <svg
          className={styles.lockIcon}
          width="34"
          height="34"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>

        <h2 className={styles.title}>{t("auth.createAccount")}</h2>
        <p className={styles.subtitle}>{t("auth.agreeToTerms")}</p>

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.buttonContainer}>
          <button
            className={styles.registerButton}
            onClick={handleRegister}
            disabled={pending}
          >
            {t("auth.register")}
          </button>

          <button
            className={styles.loginButton}
            onClick={handleLogin}
            disabled={pending}
          >
            {t("auth.login")}
          </button>

          <button
            type="button"
            className={styles.guestButton}
            onClick={handleGuest}
            disabled={pending}
          >
            {pending ? (
              <span className={styles.spinner} />
            ) : (
              "Continue as Guest"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
